// EditEventModal.jsx
import React, { useState, useEffect } from 'react';
import { trainingEventService } from '../../services/trainingEventService';
import { courseService } from '../../services/courseService';
import { useNotifyDataChange } from '../../hooks/useNotifyDataChange';
import './TrainingEvents.css';

// SVG иконки
const XIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// Конфигурация статусов согласно файлу "статусы мероприятий.txt"
export const EVENT_STATUSES = {
  DRAFT: 'draft',
  PLANNED: 'planned',
  CONFIRMED: 'confirmed',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  AWAITING_CONFIRMATION: 'awaiting_confirmation'
};

// Отображаемые названия статусов
export const STATUS_LABELS = {
  [EVENT_STATUSES.DRAFT]: 'Черновик',
  [EVENT_STATUSES.PLANNED]: 'Запланировано',
  [EVENT_STATUSES.CONFIRMED]: 'Подтверждено',
  [EVENT_STATUSES.IN_PROGRESS]: 'В процессе',
  [EVENT_STATUSES.COMPLETED]: 'Завершено',
  [EVENT_STATUSES.CANCELLED]: 'Отменено',
  [EVENT_STATUSES.AWAITING_CONFIRMATION]: 'Ожидает подтверждения'
};

// Цвета/классы для бейджей статусов
export const getStatusClass = (status) => {
  switch (status) {
    case EVENT_STATUSES.DRAFT:
      return 'status-draft';
    case EVENT_STATUSES.PLANNED:
      return 'status-planned';
    case EVENT_STATUSES.CONFIRMED:
      return 'status-confirmed';
    case EVENT_STATUSES.IN_PROGRESS:
      return 'status-in-progress';
    case EVENT_STATUSES.COMPLETED:
      return 'status-completed';
    case EVENT_STATUSES.CANCELLED:
      return 'status-cancelled';
    case EVENT_STATUSES.AWAITING_CONFIRMATION:
      return 'status-awaiting';
    default:
      return '';
  }
};

// Список статусов, доступных для ручного изменения 
export const MANUAL_STATUSES = [
  { value: EVENT_STATUSES.DRAFT, label: STATUS_LABELS[EVENT_STATUSES.DRAFT] },
  { value: EVENT_STATUSES.PLANNED, label: STATUS_LABELS[EVENT_STATUSES.PLANNED] },
  { value: EVENT_STATUSES.CONFIRMED, label: STATUS_LABELS[EVENT_STATUSES.CONFIRMED] },
  { value: EVENT_STATUSES.IN_PROGRESS, label: STATUS_LABELS[EVENT_STATUSES.IN_PROGRESS] },
  { value: EVENT_STATUSES.COMPLETED, label: STATUS_LABELS[EVENT_STATUSES.COMPLETED] },
  { value: EVENT_STATUSES.CANCELLED, label: STATUS_LABELS[EVENT_STATUSES.CANCELLED] },
  { value: EVENT_STATUSES.AWAITING_CONFIRMATION, label: STATUS_LABELS[EVENT_STATUSES.AWAITING_CONFIRMATION] }
];

const EditEventModal = ({ isOpen, event, onClose, onSuccess }) => {
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  const { notifyDataChanged } = useNotifyDataChange();
  
  const [formData, setFormData] = useState({
    title: '',
    course_id: '',
    format: 'onsite',
    start_date: '',
    end_date: '',
    location: '',
    training_center: '',
    status: 'draft',
    cost: '',
    notes: '',
    max_participants: '',
    participant_ids: []
  });

  useEffect(() => {
    if (isOpen && event) {
      loadCourses();
      setFormData({
        title: event.title || '',
        course_id: event.course_id ? String(event.course_id) : '',
        format: event.format || 'onsite',
        start_date: event.start_date || '',
        end_date: event.end_date || '',
        location: event.location || '',
        training_center: event.training_center || '',
        status: event.status || 'draft',
        cost: event.cost || '',
        notes: event.notes || '',
        max_participants: event.max_participants || '',
        participant_ids: event.participants?.map(p => p.id) || []
      });
    }
  }, [isOpen, event]);

  const loadCourses = async () => {
    setLoadingCourses(true);
    try {
      const data = await courseService.getAll();
      let coursesArray = [];
      if (Array.isArray(data)) {
        coursesArray = data;
      } else if (data && data.courses && Array.isArray(data.courses)) {
        coursesArray = data.courses;
      } else if (data && data.data && Array.isArray(data.data)) {
        coursesArray = data.data;
      }
      setCourses(coursesArray);
    } catch (err) {
      console.error('Ошибка загрузки курсов:', err);
    } finally {
      setLoadingCourses(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'course_id') {
      const selectedCourse = courses.find(course => String(course.id) === String(value));
      setFormData(prev => ({
        ...prev,
        course_id: value,
        title: selectedCourse ? selectedCourse.name : prev.title
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFormatChange = (format) => {
    setFormData(prev => ({ ...prev, format }));
  };

  const handleUpdateSubmit = async () => {
    if (!formData.title.trim()) {
      showToast('Введите название мероприятия', 'error');
      return;
    }
    if (!formData.course_id) {
      showToast('Выберите вид обучения', 'error');
      return;
    }
    if (!formData.start_date) {
      showToast('Укажите дату начала', 'error');
      return;
    }
    if (!formData.end_date) {
      showToast('Укажите дату окончания', 'error');
      return;
    }

    setUpdating(true);
    try {
      const eventData = {
        title: formData.title.trim(),
        course_id: parseInt(formData.course_id),
        format: formData.format,
        start_date: formData.start_date,
        end_date: formData.end_date,
        location: formData.location || '',
        training_center: formData.training_center || '',
        status: formData.status,
        cost: formData.cost ? parseFloat(formData.cost) : 0,
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : 0,
        notes: formData.notes || '',
        participant_ids: formData.participant_ids || []
      };

      await trainingEventService.update(event.id, eventData);
      showToast('Мероприятие успешно обновлено', 'success');
      
      notifyDataChanged('EditEventModal', {
        action: 'update',
        entity: 'trainingEvent',
        eventId: event.id,
        courseId: formData.course_id,
        title: formData.title.trim(),
        oldStatus: event.status,
        newStatus: formData.status
      });
      
      setTimeout(() => {
        onSuccess?.();
      }, 500);
    } catch (err) {
      console.error('Ошибка обновления мероприятия:', err);
      showToast(err.message || 'Ошибка при обновлении мероприятия', 'error');
    } finally {
      setUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-icon">
            {toast.type === 'success' ? <CheckIcon /> : <AlertIcon />}
          </span>
          <span className="toast-message">{toast.message}</span>
        </div>
      )}
      
      <div className="modal-content event-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Редактирование мероприятия</h3>
          <button className="modal-close" onClick={onClose} disabled={updating}>
            <XIcon />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Вид обучения *</label>
            <select
              name="course_id"
              value={formData.course_id}
              onChange={handleFormChange}
              disabled={updating || loadingCourses}
              required
            >
              <option value="">{loadingCourses ? 'Загрузка...' : 'Выберите вид обучения'}</option>
              {courses.map(course => (
                <option key={course.id} value={String(course.id)}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Название мероприятия *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleFormChange}
              placeholder="Название мероприятия"
              disabled={updating}
            />
          </div>

          <div className="form-group">
            <label>Формат *</label>
            <div className="format-switch">
              <button
                type="button"
                className={`format-option ${formData.format === 'online' ? 'active' : ''}`}
                onClick={() => handleFormatChange('online')}
                disabled={updating}
              >
                Онлайн
              </button>
              <button
                type="button"
                className={`format-option ${formData.format === 'onsite' ? 'active' : ''}`}
                onClick={() => handleFormatChange('onsite')}
                disabled={updating}
              >
                Очное (Оффлайн)
              </button>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group half">
              <label>Дата начала *</label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleFormChange}
                disabled={updating}
              />
            </div>
            <div className="form-group half">
              <label>Дата окончания *</label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleFormChange}
                disabled={updating}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Адрес/Место проведения</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleFormChange}
              placeholder="Адрес проведения"
              disabled={updating}
            />
          </div>

          <div className="form-group">
            <label>Учебный центр</label>
            <input
              type="text"
              name="training_center"
              value={formData.training_center}
              onChange={handleFormChange}
              placeholder="Учебный центр"
              disabled={updating}
            />
          </div>

          <div className="form-group">
            <label>Статус *</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleFormChange}
              disabled={updating}
            >
              {MANUAL_STATUSES.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
              Статус "Ожидает подтверждения" устанавливается автоматически и недоступен для ручного изменения
            </small>
          </div>

          <div className="form-row">
            <div className="form-group half">
              <label>Стоимость (₽)</label>
              <input
                type="number"
                name="cost"
                value={formData.cost}
                onChange={handleFormChange}
                placeholder="Стоимость"
                disabled={updating}
                step="0.01"
              />
            </div>
            <div className="form-group half">
              <label>Макс. участников</label>
              <input
                type="number"
                name="max_participants"
                value={formData.max_participants}
                onChange={handleFormChange}
                placeholder="Максимум участников"
                disabled={updating}
                step="1"
                min="0"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Заметки</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleFormChange}
              placeholder="Дополнительная информация, требования к участникам..."
              disabled={updating}
              rows="4"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button 
            className="btn-secondary" 
            onClick={onClose}
            disabled={updating}
          >
            Отмена
          </button>
          <button 
            className="btn-primary" 
            onClick={handleUpdateSubmit}
            disabled={updating}
          >
            {updating ? 'Сохранение...' : 'Сохранить изменения'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEventModal;